import { format } from 'date-fns';
import {
  ColumnMapping,
  convertExcelDate,
  createColumnMapping,
  ExcelProcessorConfig,
  formatValidationErrors,
  HeaderConfig,
  parseBoolean,
  ParsedExcelData,
  parseExcelData,
  parseNumber,
  readExcelFile,
  validateRequiredFields,
  ValidationError
} from '@/lib/excel';
import { DEFAULT_MA_NX_CODE } from '../constants';
import { roundNumber } from '@/lib/formatUtils';
import { calculateTotals } from './calc-util';
import { toast } from '@/hooks/use-toast';
import { MA_CHUNG_TU } from '@/constants';

type ExcelCellValue = string | number | boolean | Date | null | undefined;
type ExcelSection = Record<string, ExcelCellValue[]>;
type UUIDLookupSection = Record<string, Array<string | null>>;
type UUIDLookupResponse = {
  main?: UUIDLookupSection;
  chi_tiet?: UUIDLookupSection;
};
type UUIDLookupPayload = ReturnType<typeof UUIDPayload>;

const toText = (value: ExcelCellValue): string => (value === null || value === undefined ? '' : String(value));
const toRoundable = (value: ExcelCellValue): string | number | null | undefined =>
  typeof value === 'string' || typeof value === 'number' || value === null || value === undefined
    ? value
    : String(value);
const toDateCell = (value: ExcelCellValue): string | number | Date | undefined =>
  typeof value === 'string' || typeof value === 'number' || value instanceof Date ? value : undefined;

const HEADERS = {
  MA_DON_VI: 'Mã đơn vị',
  SO_CT: 'Số c/từ*',
  NGAY_CT: 'Ngày chứng từ*',
  MA_NT: 'Ngoại tệ*',
  TY_GIA: 'Tỷ giá',
  LOAI_NHAP: 'Loại nhập*',
  MA_GD: 'Giao dịch*',
  MA_KH: 'Mã đối tượng*',
  TEN_KH: 'Tên đối tượng',
  DIEN_GIAI: 'Diễn giải',
  MA_VT: 'Mã sản phẩm*',
  TEN_VT: 'Tên sản phẩm',
  DVT: 'Đvt*',
  MA_KHO: 'Mã kho*',
  MA_LO: 'Mã lô',
  MA_VI_TRI: 'Mã vị trí',
  SO_LUONG: 'Số lượng*',
  GIA_TB: 'Giá trung bình',
  GIA: 'Giá',
  TIEN: 'Tiền',
  TK_NO: 'Tk nợ',
  MA_NX: 'Lý do nhập',
  TK_CO: 'Tk có',
  MA_BP: 'Bộ phận',
  MA_SP: 'Sản phẩm'
} as const;

const HEADER_CONFIG: HeaderConfig[] = [
  { header: HEADERS.MA_DON_VI, key: 'ma_don_vi', type: 'main' },
  { header: HEADERS.SO_CT, key: 'so_ct', type: 'main' },
  { header: HEADERS.NGAY_CT, key: 'ngay_ct', type: 'main' },
  { header: HEADERS.MA_NT, key: 'ma_nt', type: 'main' },
  { header: HEADERS.TY_GIA, key: 'ty_gia', type: 'main' },
  { header: HEADERS.LOAI_NHAP, key: 'status', type: 'main' },
  { header: HEADERS.MA_GD, key: 'ma_gd', type: 'main' },
  { header: HEADERS.MA_KH, key: 'ma_kh', type: 'main' },
  { header: HEADERS.TEN_KH, key: 'ten_kh', type: 'main' },
  { header: HEADERS.DIEN_GIAI, key: 'dien_giai', type: 'main' },
  { header: HEADERS.MA_VT, key: 'ma_vt', type: 'chi_tiet' },
  { header: HEADERS.TEN_VT, key: 'ten_vt', type: 'chi_tiet' },
  { header: HEADERS.DVT, key: 'dvt', type: 'chi_tiet' },
  { header: HEADERS.MA_KHO, key: 'ma_kho', type: 'chi_tiet' },
  { header: HEADERS.MA_LO, key: 'ma_lo', type: 'chi_tiet' },
  { header: HEADERS.MA_VI_TRI, key: 'ma_vi_tri', type: 'chi_tiet' },
  { header: HEADERS.SO_LUONG, key: 'so_luong', type: 'chi_tiet' },
  { header: HEADERS.GIA_TB, key: 'pn_tb', type: 'chi_tiet' },
  { header: HEADERS.GIA, key: 'gia', type: 'chi_tiet' },
  { header: HEADERS.TIEN, key: 'tien', type: 'chi_tiet' },
  { header: HEADERS.TK_NO, key: 'tk_vt', type: 'chi_tiet' },
  { header: HEADERS.MA_NX, key: 'ma_nx', type: 'chi_tiet' },
  { header: HEADERS.TK_CO, key: 'tk_du', type: 'chi_tiet' },
  { header: HEADERS.MA_BP, key: 'ma_bp', type: 'chi_tiet' },
  { header: HEADERS.MA_SP, key: 'ma_sp', type: 'chi_tiet' }
];

const UUID_FIELDS = {
  main: ['ma_nt', 'ma_kh', 'ma_gd'],
  chi_tiet: ['ma_vt', 'dvt', 'ma_kho', 'ma_lo', 'ma_vi_tri', 'tk_vt', 'tk_du', 'ma_nx', 'ma_bp', 'ma_sp']
};

const getFieldName = (key: string) => {
  const config = HEADER_CONFIG.find(c => c.key === key);
  return config?.header?.replace('*', '').trim() || key;
};

const getFieldValues = <T>(...candidates: Array<T[] | undefined>): T[] => {
  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      const hasMeaningfulValue = candidate.some(
        value => value !== undefined && value !== null && String(value).trim() !== ''
      );
      if (hasMeaningfulValue) {
        return candidate;
      }
    }
  }
  return [];
};

const getFieldValuesWithFallback = <T>(candidates: T[] | undefined, fallback: T[]): T[] => {
  const values = getFieldValues(candidates);
  return values.length > 0 ? values : fallback;
};

const hasData = (item: Record<string, unknown>, keys: string[]) =>
  keys.some(k => item[k] != null && item[k] !== '' && item[k] !== 0);

const UUIDPayload = (data: ParsedExcelData) => {
  const build = (section: ExcelSection, fields: string[]) =>
    Object.fromEntries(fields.map(f => [f, section?.[f] || []]));

  const main = data.main || {};
  const chi_tiet = data.chi_tiet || {};

  const seenDates = new Set<string>();
  const chungTuArr: Array<{ ma_ct: string; ngay_hl: string }> = [];

  (main.so_ct || []).forEach((_, index: number) => {
    const ngay = convertExcelDate(main.ngay_ct?.[index]) || format(new Date(), 'yyyy-MM-dd');
    const ngay_hl = ngay || format(new Date(), 'yyyy-MM-dd');
    if (seenDates.has(ngay_hl)) return;
    seenDates.add(ngay_hl);

    chungTuArr.push({
      ma_ct: MA_CHUNG_TU.TON_KHO.PHIEU_NHAP_KHO,
      ngay_hl
    });
  });

  return {
    main: {
      ...build(main, UUID_FIELDS.main),
      ma_gd: (main.ma_gd || []).map(val => (val ? `${val}` : val))
    },
    chi_tiet: {
      ...build(chi_tiet, UUID_FIELDS.chi_tiet),
      ma_nx: getFieldValuesWithFallback(chi_tiet.ma_nx, [DEFAULT_MA_NX_CODE])
    },
    chung_tu: chungTuArr
  };
};

const ValidateUUID = (
  response: UUIDLookupResponse,
  columnMapping: Record<string, ColumnMapping>
): ValidationError[] => {
  if (!response) return [];
  const errors: ValidationError[] = [];

  (['main', 'chi_tiet'] as const).forEach(section => {
    const data = response[section];
    if (!data) return;

    Object.entries(data).forEach(([field, values]) => {
      if (!Array.isArray(values)) return;
      const mapping = Object.values(columnMapping).find(m => m.key === field && m.type === section);

      values.forEach((val, i) => {
        if (val === null) {
          errors.push({
            row: i + 2,
            field,
            message: `${getFieldName(field)} không hợp lệ`,
            section,
            columnIndex: mapping?.index
          });
        }
      });
    });
  });

  return errors;
};

const processChiTietRows = (
  groupedRecords: Map<string, ImportBody>,
  main: ExcelSection,
  chi_tiet: ExcelSection,
  UUIDResponse: UUIDLookupResponse,
  rowCount: number
): Map<string, ImportBody['chi_tiet']> => {
  const chiTietMap = new Map<string, ImportBody['chi_tiet']>();
  const detailKeys = HEADER_CONFIG.filter(c => c.type === 'chi_tiet').map(c => c.key);

  for (let i = 0; i < rowCount; i++) {
    const documentNumber = toText(main.so_ct?.[i]).trim();
    const recordKey = `${documentNumber}`;

    const record = groupedRecords.get(recordKey);
    if (!record) continue;

    const description = toText(main.dien_giai?.[i]).trim();
    if ((!record.dien_giai || record.dien_giai.trim() === '') && description) {
      record.dien_giai = description;
    }

    const quantity = roundNumber(toRoundable(chi_tiet.so_luong?.[i]), 6);

    const detailsForKey = chiTietMap.get(recordKey) || [];

    const detailItem = {
      line: detailsForKey.length + 1,
      ma_vt: UUIDResponse.chi_tiet?.ma_vt?.[i] || null,
      dvt: UUIDResponse.chi_tiet?.dvt?.[i] || UUIDResponse.chi_tiet?.ma_vt_dvt?.[i] || null,
      ma_kho: UUIDResponse.chi_tiet?.ma_kho?.[i] || null,
      ma_lo: UUIDResponse.chi_tiet?.ma_lo?.[i] || null,
      ma_vi_tri: UUIDResponse.chi_tiet?.ma_vi_tri?.[i] || null,
      so_luong: quantity,
      pn_tb: parseBoolean(toText(chi_tiet.pn_tb?.[i])),
      gia: roundNumber(toRoundable(chi_tiet.gia?.[i]), 6),
      gia_nt: roundNumber(toRoundable(chi_tiet.gia?.[i]), 6),
      tien: roundNumber(toRoundable(chi_tiet.tien?.[i]), 6),
      tien_nt: roundNumber(toRoundable(chi_tiet.tien?.[i]), 6),
      tk_vt: UUIDResponse.chi_tiet?.tk_vt?.[i] || null,
      ma_nx: UUIDResponse.chi_tiet?.ma_nx?.[i] || null,
      tk_du: UUIDResponse.chi_tiet?.tk_du?.[i] || null,
      ma_bp: UUIDResponse.chi_tiet?.ma_bp?.[i] || null,
      ma_sp: UUIDResponse.chi_tiet?.ma_sp?.[i] || null
    };

    if (hasData(detailItem, detailKeys)) {
      detailsForKey.push(detailItem);
      chiTietMap.set(recordKey, detailsForKey);
    }
  }

  return chiTietMap;
};

const processMainRows = (
  main: ExcelSection,
  UUIDResponse: UUIDLookupResponse,
  entityUnitUUID: string,
  rowCount: number,
  defaultMaGD: string,
  chiTietMap?: Map<string, ImportBody['chi_tiet']>
): Map<string, ImportBody> => {
  const groupedRecords = new Map<string, ImportBody>();

  for (let i = 0; i < rowCount; i++) {
    const documentNumber = toText(main.so_ct?.[i]).trim();
    const recordKey = `${documentNumber}`;

    if (!groupedRecords.has(recordKey)) {
      const documentDate = convertExcelDate(toDateCell(main.ngay_ct?.[i])) || format(new Date(), 'yyyy-MM-dd');
      const transactionDate = documentDate;

      const detail = chiTietMap?.get(recordKey) || [];
      const totals = calculateTotals(detail);

      const record: ImportBody = {
        so_ct: documentNumber || undefined,
        ngay_ct: documentDate,
        ngay_lct: transactionDate,
        ma_nt: UUIDResponse.main?.ma_nt?.[i] || toText(main.ma_nt?.[i]) || 'VND',
        ty_gia: toText(main.ty_gia?.[i]) || '1',
        ma_gd: UUIDResponse.main?.ma_gd?.[i] || toText(main.ma_gd?.[i]) || defaultMaGD,
        ma_kh: UUIDResponse.main?.ma_kh?.[i] || toText(main.ma_kh?.[i]) || '',
        ten_kh: toText(main.ten_kh?.[i]) || undefined,
        dien_giai: toText(main.dien_giai?.[i]).trim(),
        unit_id: entityUnitUUID,
        ma_ngv: '5',
        status: toText(main.status?.[i]) || '0',
        i_so_ct: 0,
        action: 'create',
        id_progress: 0,
        chi_tiet: detail,
        t_so_luong: roundNumber(totals.t_so_luong, 6),
        t_tien: roundNumber(totals.t_tien, 6),
        t_tien_nt: roundNumber(totals.t_tien_nt, 6)
      };

      groupedRecords.set(recordKey, record);
    }
  }

  return groupedRecords;
};

const TransformToBodies = (
  dataRows: ExcelCellValue[][],
  parsedData: ParsedExcelData,
  uuidResponse: UUIDLookupResponse,
  context: { entityUnitUUID: string; defaultMaGD?: string }
): ImportBody[] => {
  const main = parsedData.main || {};
  const chi_tiet = parsedData.chi_tiet || {};
  const rowCount = dataRows.length;
  const defaultMaGD = context.defaultMaGD || 'NB';

  const tempGroupedRecords = processMainRows(main, uuidResponse, context.entityUnitUUID, rowCount, defaultMaGD);
  const chiTietMap = processChiTietRows(tempGroupedRecords, main, chi_tiet, uuidResponse, rowCount);
  const groupedRecords = processMainRows(main, uuidResponse, context.entityUnitUUID, rowCount, defaultMaGD, chiTietMap);

  const records = Array.from(groupedRecords.values());
  records.forEach(record => {
    const key = `${record.so_ct || ''}`.trim();
    const chiTiet = chiTietMap.get(key) || [];
    record.chi_tiet = chiTiet;

    const totals = calculateTotals(chiTiet);
    record.t_so_luong = roundNumber(totals.t_so_luong, 6);
    record.t_tien_nt = roundNumber(totals.t_tien_nt, 6);
    record.t_tien = roundNumber(totals.t_tien, 6);
  });

  return records;
};

export interface ImportBody {
  so_ct?: string;
  ngay_ct?: string;
  ngay_lct?: string;
  ma_nt?: string;
  ty_gia?: string;
  ma_gd?: string;
  ma_kh?: string;
  ten_kh?: string;
  dien_giai?: string;
  unit_id: string;
  ma_ngv: string;
  status?: string;
  i_so_ct?: number;
  chung_tu?: string | null;
  ma_nk?: string | null;
  t_tien?: number;
  t_tien_nt?: number;
  t_so_luong?: number;
  action?: string;
  id_progress?: number;
  chi_tiet: Array<{
    line: number;
    ma_vt: string | null;
    dvt: string | null;
    ma_kho: string | null;
    ma_lo?: string | null;
    ma_vi_tri?: string | null;
    so_luong: number;
    pn_tb?: boolean;
    gia?: number;
    tien?: number;
    tk_vt?: string | null;
    ma_nx?: string | null;
    tk_du?: string | null;
    ma_bp?: string | null;
    ma_sp?: string | null;
  }>;
}

export interface ImportErrorData {
  errors: ValidationError[];
  columnMapping: Record<string, ColumnMapping>;
  errorFileUrl?: string;
  errorFileName?: string;
}

export const ExcelConfig: ExcelProcessorConfig<ImportBody> = {
  Headers: HEADERS,
  HeaderConfig: HEADER_CONFIG,
  Transform: TransformToBodies,
  UUIDPayload,
  ValidateUUID
};

export const handleImportExcel = async (
  getUUIDByCode: (payload: UUIDLookupPayload) => Promise<UUIDLookupResponse>,
  onSubmit: (bodies: ImportBody[]) => Promise<void>,
  entityUnitUUID: string,
  onError?: (errorData: ImportErrorData) => void
): Promise<void> => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.xlsx,.xls';

  input.onchange = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;

    try {
      const { headers, dataRows } = await readExcelFile(file);

      if (dataRows.length === 0) {
        toast({
          description: 'File Excel không có dữ liệu',
          variant: 'destructive'
        });
        return;
      }

      const columnMapping = createColumnMapping(headers, ExcelConfig.HeaderConfig);
      const parsedData = parseExcelData(columnMapping, dataRows);

      const emitErrors = (validationErrors: ValidationError[]) => {
        if (onError) {
          onError({ errors: validationErrors, columnMapping });
        } else {
          const errorMessage = formatValidationErrors(validationErrors);
          toast({
            description: errorMessage,
            variant: 'destructive'
          });
        }
      };

      const aggregatedErrors: ValidationError[] = [];
      const requiredFieldErrors = validateRequiredFields(columnMapping, dataRows);

      if (requiredFieldErrors.length > 0) {
        aggregatedErrors.push(...requiredFieldErrors);
      }

      const uuidPayload = ExcelConfig.UUIDPayload?.(parsedData);
      const UUIDResponse = uuidPayload ? await getUUIDByCode(uuidPayload) : null;
      if (!UUIDResponse) {
        toast({
          description: 'Không nhận được UUID response',
          variant: 'destructive'
        });
        return;
      }

      if (ExcelConfig.ValidateUUID) {
        const validationErrors = ExcelConfig.ValidateUUID(UUIDResponse, columnMapping);
        if (validationErrors.length > 0) {
          aggregatedErrors.push(...validationErrors);
        }
      }

      if (aggregatedErrors.length > 0) {
        emitErrors(aggregatedErrors);
        return;
      }

      const bodies = ExcelConfig.Transform(dataRows, parsedData, UUIDResponse, { entityUnitUUID });
      await onSubmit(bodies);
    } catch (error) {
      console.error('Lỗi khi đọc file Excel:', error);
      toast({
        description: error instanceof Error ? error.message : 'Có lỗi xảy ra khi nhập dữ liệu Excel',
        variant: 'destructive'
      });
    }
  };

  input.click();
};
